/**
 * Module Manifest Parsing and Validation
 *
 * Functions for creating, parsing, and validating AOS module manifests.
 */

import { sha256 } from '@noble/hashes/sha256';
import { base58 } from '../identity/base58.js';
import {
  createMultiSigEnvelopeFromObject,
  addSignature,
  verifyMultiSig,
  createPolicy,
  type MultiSigPolicy,
  type MultiSigEnvelope,
} from '../identity/multisig.js';
import type { KeyPair } from '../identity/keys.js';
import {
  type ModuleManifest,
  type UnsignedModuleManifest,
  type ModuleAuthor,
  type ModulePermission,
  type ManifestValidationResult,
  type ModuleVerifyResult,
  type PermissionCheckResult,
  PERMISSION_CATEGORIES,
} from './types.js';

// ============================================================================
// Validation
// ============================================================================

/** Valid module ID pattern: lowercase alphanumeric + hyphens */
const MODULE_ID_REGEX = /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/;

/** Semver pattern */
const SEMVER_REGEX = /^\d+\.\d+\.\d+(-[a-zA-Z0-9.-]+)?(\+[a-zA-Z0-9.-]+)?$/;

/** Base58 pattern (rough check) */
const BASE58_REGEX = /^[1-9A-HJ-NP-Za-km-z]+$/;

/** SHA-256 hex pattern */
const SHA256_HEX_REGEX = /^[a-f0-9]{64}$/;

/**
 * Validate a module ID
 */
export function isValidModuleId(id: string): boolean {
  if (!id || typeof id !== 'string') return false;
  if (id.length < 1 || id.length > 64) return false;
  return MODULE_ID_REGEX.test(id);
}

/**
 * Validate a semantic version string
 */
export function isValidSemver(version: string): boolean {
  if (!version || typeof version !== 'string') return false;
  return SEMVER_REGEX.test(version);
}

/**
 * Validate a base58 encoded string
 */
export function isValidBase58(str: string): boolean {
  if (!str || typeof str !== 'string') return false;
  if (str.length < 1 || str.length > 128) return false;
  return BASE58_REGEX.test(str);
}

/**
 * Validate a SHA-256 hash (hex encoded)
 */
export function isValidSha256Hex(hash: string): boolean {
  if (!hash || typeof hash !== 'string') return false;
  return SHA256_HEX_REGEX.test(hash);
}

/**
 * Validate a permission string
 */
export function isValidPermission(perm: string): perm is ModulePermission {
  return perm in PERMISSION_CATEGORIES;
}

/**
 * Validate module author
 */
function validateAuthor(author: unknown): string[] {
  const errors: string[] = [];

  if (!author || typeof author !== 'object') {
    errors.push('author must be an object');
    return errors;
  }

  const a = author as Record<string, unknown>;

  if (!a.pubkey || typeof a.pubkey !== 'string') {
    errors.push('author.pubkey is required');
  } else if (!isValidBase58(a.pubkey)) {
    errors.push('author.pubkey must be valid base58');
  }

  if (a.alias !== undefined && typeof a.alias !== 'string') {
    errors.push('author.alias must be a string');
  }

  if (a.org !== undefined && typeof a.org !== 'string') {
    errors.push('author.org must be a string');
  }

  return errors;
}

/**
 * Validate chain information
 */
function validateChain(chain: unknown): string[] {
  const errors: string[] = [];

  if (!chain || typeof chain !== 'object') {
    errors.push('chain must be an object');
    return errors;
  }

  const c = chain as Record<string, unknown>;

  if (c.previous !== null && typeof c.previous !== 'string') {
    errors.push('chain.previous must be null or string');
  } else if (c.previous !== null && !isValidBase58(c.previous as string)) {
    errors.push('chain.previous must be valid base58');
  }

  if (typeof c.sequence !== 'number' || !Number.isInteger(c.sequence) || c.sequence < 0) {
    errors.push('chain.sequence must be a non-negative integer');
  }

  if (typeof c.changelog !== 'string') {
    errors.push('chain.changelog must be a string');
  }

  return errors;
}

/**
 * Validate a module manifest structure (without verifying signatures)
 */
export function validateManifest(manifest: unknown): ManifestValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!manifest || typeof manifest !== 'object') {
    return { valid: false, errors: ['manifest must be an object'], warnings: [] };
  }

  const m = manifest as Record<string, unknown>;

  // Type
  if (m.type !== 'aos.module') {
    errors.push('type must be "aos.module"');
  }

  // ID
  if (!m.id || typeof m.id !== 'string') {
    errors.push('id is required');
  } else if (!isValidModuleId(m.id)) {
    errors.push('id must be lowercase alphanumeric with hyphens, starting with letter');
  }

  // Version
  if (!m.version || typeof m.version !== 'string') {
    errors.push('version is required');
  } else if (!isValidSemver(m.version)) {
    errors.push('version must be valid semver (e.g., 1.0.0)');
  }

  // Name
  if (!m.name || typeof m.name !== 'string') {
    errors.push('name is required');
  } else if (m.name.length > 100) {
    errors.push('name must be 100 characters or less');
  }

  // Description
  if (!m.description || typeof m.description !== 'string') {
    errors.push('description is required');
  } else if (m.description.length > 1000) {
    warnings.push('description is longer than recommended (1000 chars)');
  }

  // Author
  errors.push(...validateAuthor(m.author));

  // Created
  if (typeof m.created !== 'number' || !Number.isInteger(m.created)) {
    errors.push('created must be a timestamp integer');
  } else if (m.created < 0 || m.created > Date.now() + 86400000) {
    warnings.push('created timestamp seems invalid');
  }

  // Entry
  if (!m.entry || typeof m.entry !== 'string') {
    errors.push('entry (DAG hash) is required');
  } else if (!isValidBase58(m.entry)) {
    errors.push('entry must be valid base58 DAG hash');
  }

  // Chunks (optional)
  if (m.chunks !== undefined) {
    if (!Array.isArray(m.chunks)) {
      errors.push('chunks must be an array');
    } else {
      for (let i = 0; i < m.chunks.length; i++) {
        if (!isValidBase58(m.chunks[i])) {
          errors.push(`chunks[${i}] must be valid base58 DAG hash`);
        }
      }
    }
  }

  // Size
  if (typeof m.size !== 'number' || !Number.isInteger(m.size) || m.size < 0) {
    errors.push('size must be a non-negative integer');
  } else if (m.size > 50 * 1024 * 1024) {
    warnings.push('module size exceeds 50MB');
  }

  // Content hash
  if (!m.contentHash || typeof m.contentHash !== 'string') {
    errors.push('contentHash is required');
  } else if (!isValidSha256Hex(m.contentHash)) {
    errors.push('contentHash must be 64-character hex SHA-256');
  }

  // Permissions
  if (!Array.isArray(m.permissions)) {
    errors.push('permissions must be an array');
  } else {
    const seen = new Set<string>();
    for (const perm of m.permissions) {
      if (typeof perm !== 'string') {
        errors.push('each permission must be a string');
      } else if (!isValidPermission(perm)) {
        warnings.push(`unknown permission: ${perm}`);
      } else if (seen.has(perm)) {
        warnings.push(`duplicate permission: ${perm}`);
      }
      seen.add(perm);
    }
  }

  // Signatures
  if (!m.signatures || typeof m.signatures !== 'object') {
    errors.push('signatures envelope is required');
  }

  // Chain
  errors.push(...validateChain(m.chain));

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// Creation
// ============================================================================

/**
 * Options for creating a module manifest
 */
export interface CreateModuleManifestOptions {
  id: string;
  version: string;
  name: string;
  description: string;
  author: ModuleAuthor;
  entry: string;
  chunks?: string[];
  size: number;
  contentHash: string;
  permissions: ModulePermission[];
  signingPolicy: MultiSigPolicy;
  chain?: {
    previous: string | null;
    sequence: number;
    changelog: string;
  };
  meta?: ModuleManifest['meta'];
}

/**
 * Create an unsigned module manifest
 */
export function createUnsignedManifest(options: CreateModuleManifestOptions): UnsignedModuleManifest {
  const {
    id,
    version,
    name,
    description,
    author,
    entry,
    chunks,
    size,
    contentHash,
    permissions,
    chain,
    meta,
  } = options;

  const manifest: UnsignedModuleManifest = {
    type: 'aos.module',
    id,
    version,
    name,
    description,
    author,
    created: Date.now(),
    entry,
    size,
    contentHash,
    permissions,
    chain: chain ?? {
      previous: null,
      sequence: 0,
      changelog: 'Initial release',
    },
  };

  if (chunks && chunks.length > 0) {
    manifest.chunks = chunks;
  }

  if (meta) {
    manifest.meta = meta;
  }

  return manifest;
}

/**
 * Create a module manifest with an empty signature envelope
 */
export function createModuleManifest(options: CreateModuleManifestOptions): ModuleManifest {
  const unsigned = createUnsignedManifest(options);

  // Create the signing envelope from the unsigned manifest
  const signatures = createMultiSigEnvelopeFromObject(unsigned, options.signingPolicy);

  return {
    ...unsigned,
    signatures,
  };
}

/**
 * Sign a module manifest with a key
 */
export function signModuleManifest(manifest: ModuleManifest, keyPair: KeyPair): ModuleManifest {
  const newSignatures = addSignature(manifest.signatures, keyPair);
  return {
    ...manifest,
    signatures: newSignatures,
  };
}

// ============================================================================
// Verification
// ============================================================================

/**
 * Verify module manifest signatures
 *
 * @param manifest - The manifest to verify
 * @param trustedKeys - Set of public keys we trust
 * @param revokedKeys - Optional set of revoked keys to reject
 */
export function verifyModuleManifest(
  manifest: ModuleManifest,
  trustedKeys: Set<string>,
  revokedKeys?: Set<string>
): ModuleVerifyResult {
  // First validate structure
  const validation = validateManifest(manifest);
  if (!validation.valid) {
    return {
      valid: false,
      validCount: 0,
      threshold: manifest.signatures?.policy?.threshold ?? 0,
      trustedSigners: [],
      signatureDetails: [],
      errors: validation.errors,
    };
  }

  // Verify multi-sig
  const multiSigResult = verifyMultiSig(manifest.signatures, revokedKeys);

  // Check which valid signers are trusted
  const trustedSigners = multiSigResult.signatureDetails
    .filter(d => d.valid && d.authorized && trustedKeys.has(d.signer))
    .map(d => d.signer);

  const signatureDetails = multiSigResult.signatureDetails.map(d => ({
    signer: d.signer,
    valid: d.valid,
    trusted: trustedKeys.has(d.signer),
    error: d.error,
  }));

  // Valid if multi-sig passes AND at least one trusted signer
  const valid = multiSigResult.valid && trustedSigners.length > 0;

  const errors = [...multiSigResult.errors];
  if (multiSigResult.valid && trustedSigners.length === 0) {
    errors.push('No signatures from trusted keys');
  }

  return {
    valid,
    validCount: multiSigResult.validCount,
    threshold: multiSigResult.threshold,
    trustedSigners,
    signatureDetails,
    errors,
  };
}

// ============================================================================
// Permission Checking
// ============================================================================

/**
 * Check module permissions and categorize them
 */
export function checkPermissions(permissions: ModulePermission[]): PermissionCheckResult {
  const result: PermissionCheckResult = {
    safe: [],
    prompt: [],
    dangerous: [],
    unknown: [],
  };

  for (const perm of permissions) {
    if (!isValidPermission(perm)) {
      result.unknown.push(perm);
      continue;
    }

    const category = PERMISSION_CATEGORIES[perm];
    switch (category) {
      case 'safe':
        result.safe.push(perm);
        break;
      case 'prompt':
        result.prompt.push(perm);
        break;
      case 'dangerous':
        result.dangerous.push(perm);
        break;
    }
  }

  return result;
}

/**
 * Check if a permission is auto-grantable (safe)
 */
export function isAutoGrantable(permission: ModulePermission): boolean {
  return PERMISSION_CATEGORIES[permission] === 'safe';
}

/**
 * Get the auto-grantable permissions from a list
 */
export function getAutoGrantablePermissions(permissions: ModulePermission[]): ModulePermission[] {
  return permissions.filter(isAutoGrantable);
}

// ============================================================================
// Serialization
// ============================================================================

/**
 * Serialize a module manifest to JSON
 */
export function serializeManifest(manifest: ModuleManifest): string {
  return JSON.stringify(manifest, null, 2);
}

/**
 * Parse a module manifest from JSON
 */
export function parseManifest(json: string): ModuleManifest {
  const parsed = JSON.parse(json);
  const validation = validateManifest(parsed);

  if (!validation.valid) {
    throw new Error(`Invalid manifest: ${validation.errors.join(', ')}`);
  }

  return parsed as ModuleManifest;
}

/**
 * Try to parse a manifest, returning null on failure
 */
export function tryParseManifest(json: string): ModuleManifest | null {
  try {
    return parseManifest(json);
  } catch {
    return null;
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Compute content hash from code chunks
 */
export function computeContentHash(chunks: string[]): string {
  const combined = chunks.join('');
  const hash = sha256(new TextEncoder().encode(combined));
  return Array.from(hash).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Compare two semver versions
 * Returns: -1 if a < b, 0 if a == b, 1 if a > b
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const parseVersion = (v: string) => {
    const [version] = v.split('-'); // Ignore prerelease
    return version.split('.').map(Number);
  };

  const [aMajor, aMinor, aPatch] = parseVersion(a);
  const [bMajor, bMinor, bPatch] = parseVersion(b);

  if (aMajor !== bMajor) return aMajor < bMajor ? -1 : 1;
  if (aMinor !== bMinor) return aMinor < bMinor ? -1 : 1;
  if (aPatch !== bPatch) return aPatch < bPatch ? -1 : 1;
  return 0;
}

/**
 * Check if this is a valid update (new version > old version, sequence incremented)
 */
export function isValidUpdate(
  oldManifest: ModuleManifest,
  newManifest: ModuleManifest
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (oldManifest.id !== newManifest.id) {
    errors.push('Module ID mismatch');
  }

  if (compareSemver(newManifest.version, oldManifest.version) <= 0) {
    errors.push('New version must be greater than old version');
  }

  if (newManifest.chain.sequence !== oldManifest.chain.sequence + 1) {
    errors.push('Chain sequence must increment by 1');
  }

  // Note: We don't verify chain.previous here because the caller
  // needs to compute the DAG hash of oldManifest to compare

  return {
    valid: errors.length === 0,
    errors,
  };
}
